/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { alias } from '@ember/object/computed';
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import config from '@linc-technologies/linced-common/utils/config';
import { mapBy } from '@linc-technologies/linced-common/utils/arrays';

export default class NotificationsService extends Service {
  @service
  fetch;

  @service
  session;

  @service
  store;

  // Refresh interval of 2 minutes in seconds
  refreshInterval = 60 * 2;

  refreshIntervalId = 0;

  // Are we currently marking something as read?
  @tracked
  markReadInProgress = false;

  // Are we currently refreshing notifications?
  @tracked
  isRefreshing = false;

  // Will be replaced with a store query on init.
  @tracked
  rawUnreadPostNotifications = [];

  @computed('rawUnreadPostNotifications.[]')
  get unreadPostNotifications() {
    return this.rawUnreadPostNotifications.filter(
      (notification) => notification.entity,
    );
  }

  @tracked
  unreadOtherNotifications = [];

  @alias('unreadPostNotifications.length')
  numUnreadPost;

  @alias('unreadOtherNotifications.length')
  numUnreadOther;

  @computed('numUnreadPost', 'numUnreadOther')
  get totalUnread() {
    return this.numUnreadPost + this.numUnreadOther;
  }

  async init() {
    super.init(...arguments);

    // ensure current user is setup
    await this.session.ensure();

    // The service can be destroyed while ensure() waits. willDestroy has then
    // already run, so a listener or interval added now would never be removed.
    if (this.isDestroying || this.isDestroyed) {
      return;
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.refreshIntervalId = setInterval(
      () => this.refresh(),
      1000 * this.refreshInterval,
    );

    this.refresh();
  }

  // An arrow function, so willDestroy removes the same function that init
  // added.
  onVisibilityChange = () => {
    if (document.hidden) {
      return;
    }
    // call refresh as soon as tab is shown
    this.refresh();
  };

  refresh() {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }

    if (document.hidden) {
      // don't refresh if window/tab is hidden
      return;
    }

    if (isEmpty(this.session.activeSchool)) {
      // no school set, no point sending any requests
      return;
    }

    this.isRefreshing = true;

    // Actually refresh the list of unread post notifications.
    let postRefresh;
    if (
      this.rawUnreadPostNotifications &&
      this.rawUnreadPostNotifications.update
    ) {
      postRefresh = this.rawUnreadPostNotifications.update();
    } else {
      postRefresh = this.store
        .query('posts/feed-entry', {
          perPage: 10,
          unread: true,
        })
        .then((entries) => {
          this.rawUnreadPostNotifications = entries;
        });
    }

    // Refresh and fetch other types of unread notifications
    let otherRefresh;
    if (this.unreadOtherNotifications && this.unreadOtherNotifications.update) {
      otherRefresh = this.unreadOtherNotifications.update();
    } else {
      let url = `${config.APP.apiHost}/notifications/${config.APP.apiPath}/latest?unread=true&nonPost=true&limit=5`;
      otherRefresh = this.fetch.request(url).then((response) => {
        if (!isEmpty(response.results)) {
          let notificationIds = mapBy(response.results, 'notificationId');
          return this.store
            .query('notifications/notification', {
              notificationIds: JSON.stringify(notificationIds),
            })
            .then((notifications) => {
              let unreadOtherNotifications = [];
              let notification;
              response.results.forEach((result) => {
                notification = notifications.find(
                  (n) => n.id === result.notificationId,
                );

                let time = notification.createTime
                  ? notification.createTime
                  : notification.updateTime;

                unreadOtherNotifications.push({
                  subject: notification.params.subject,
                  message: notification.params.message,
                  entityId: notification.entityId,
                  personId: notification.params.personId,
                  content: notification.params.content,
                  resultId: result.id,
                  date: time,
                  type: result.notificationType,
                  isBookingOrEvent: true,
                  schoolId: result.schoolId,
                });
              });
              this.unreadOtherNotifications = unreadOtherNotifications;
            });
        } else {
          this.unreadOtherNotifications = [];
        }
      });
    }

    Promise.allSettled([postRefresh, otherRefresh]).finally(() => {
      this.isRefreshing = false;
    });
  }

  markRead(entityIds = null, resultIds = null) {
    this.markReadInProgress = true;

    let data = {
      entityIds,
      resultIds,
    };
    let options = {
      data: JSON.stringify(data),
    };
    let url = `${config.APP.apiHost}/notifications/${config.APP.apiPath}/results/:read`;
    this.fetch
      .post(url, options)
      .then(() => {
        // Successfully marked as read.
        this.markReadInProgress = false;
        this.refresh();
      })
      .catch(() => {
        // Failed.  Clear in-progress flag so the user can try again.
        this.markReadInProgress = false;
      });
  }

  getAllUnread() {
    let url = `${config.APP.apiHost}/notifications/${config.APP.apiPath}/latest?unread=true`;
    return this.fetch.request(url);
  }

  markAllRead() {
    let data = {
      all: true,
    };
    let options = {
      data: JSON.stringify(data),
    };
    let url = `${config.APP.apiHost}/notifications/${config.APP.apiPath}/results/:read`;
    return this.fetch.post(url, options);
  }

  markEntitiesRead(entityIds) {
    this.markRead(entityIds);
  }

  markEntityRead(entityId) {
    this.markRead([entityId]);
  }

  markResultsRead(resultIds) {
    this.markRead(null, resultIds);
  }

  markResultRead(resultId) {
    this.markRead(null, [resultId]);
  }

  willDestroy() {
    super.willDestroy(...arguments);
    clearInterval(this.refreshIntervalId);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }
}
